import { TextureLoader, SRGBColorSpace } from 'three';

class TextureCache {
  constructor() {
    this.cache = new Map();
    this.loader = new TextureLoader();
    this.loadingPromises = new Map();
  }

  load(url) {
    // Return cached texture as resolved Promise if available
    if (this.cache.has(url)) {
      return Promise.resolve(this.cache.get(url));
    }

    // Return in-flight promise if texture is currently loading
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    // Start loading texture
    const loadPromise = new Promise((resolve, reject) => {
      this.loader.load(
        url,
        (texture) => {
          texture.colorSpace = SRGBColorSpace;
          this.cache.set(url, texture);
          this.loadingPromises.delete(url);
          resolve(texture);
        },
        undefined,
        (error) => {
          console.warn('Failed to load texture:', url, error);
          this.loadingPromises.delete(url);
          reject(error);
        }
      );
    });

    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }

  // Get texture synchronously if cached
  get(url) {
    return this.cache.get(url);
  }

  // Check if texture is cached
  has(url) {
    return this.cache.has(url);
  }

  // Clear cache
  clear() {
    this.cache.forEach((texture) => {
      if (texture && texture.dispose) {
        texture.dispose();
      }
    });
    this.cache.clear();
    this.loadingPromises.clear();
  }

  // Remove specific texture from cache
  remove(url) {
    const texture = this.cache.get(url);
    if (texture && texture.dispose) {
      texture.dispose();
    }
    this.cache.delete(url);
  }

  // Get cache size
  size() {
    return this.cache.size;
  }
}

// Export singleton instance
export const textureCache = new TextureCache();
